import * as React from 'react'

import { ToggleGroup as ToggleGroupPrimitive } from 'radix-ui'

import { cn } from './cn'

export interface ToggleButtonGroupProps {
  value?: string | string[] | null
  exclusive?: boolean
  onChange?: (
    event: React.SyntheticEvent | undefined,
    value: string | string[] | null,
  ) => void
  size?: 'small' | 'medium' | 'large'
  color?: string
  fullWidth?: boolean
  className?: string
  children?: React.ReactNode
  'aria-label'?: string
}

export const ToggleButtonGroup = ({
  value,
  exclusive,
  onChange,
  size,
  color,
  fullWidth,
  className,
  children,
  ...props
}: ToggleButtonGroupProps) => {
  const shared = {
    className: cn(
      'inline-flex gap-1 rounded-field border border-divider bg-surface p-1',
      fullWidth && 'flex w-full',
      className,
    ),
    ...props,
  }

  if (exclusive) {
    return (
      <ToggleGroupPrimitive.Root
        type="single"
        value={(value as string) ?? ''}
        onValueChange={(v) => onChange?.(undefined, v || null)}
        {...shared}
      >
        {children}
      </ToggleGroupPrimitive.Root>
    )
  }
  return (
    <ToggleGroupPrimitive.Root
      type="multiple"
      value={(value as string[]) ?? []}
      onValueChange={(v) => onChange?.(undefined, v)}
      {...shared}
    >
      {children}
    </ToggleGroupPrimitive.Root>
  )
}

export interface ToggleButtonProps
  extends React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Item> {
  size?: 'small' | 'medium' | 'large'
}

export const ToggleButton = React.forwardRef<
  HTMLButtonElement,
  ToggleButtonProps
>(({ className, size = 'medium', ...props }, ref) => (
  <ToggleGroupPrimitive.Item
    ref={ref}
    className={cn(
      'inline-flex grow cursor-pointer items-center justify-center gap-2 border-r border-divider last:border-r-0',
      'text-sm font-medium uppercase text-text-secondary transition-colors',
      size === 'small'
        ? 'px-2 py-1'
        : size === 'large'
          ? 'px-4 py-2.5'
          : 'px-3 py-1.5',
      'hover:bg-black/5 dark:hover:bg-white/10',
      'data-[state=on]:bg-primary/15 data-[state=on]:text-primary',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/50',
      'disabled:pointer-events-none disabled:opacity-40',
      className,
    )}
    {...props}
  />
))
ToggleButton.displayName = 'ToggleButton'
